import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Alert } from '../components/Alert';

type BillingInterval = 'monthly' | 'annual';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  name: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  priceLabel: string;
  interval: string;
  description: string;
  features: PlanFeature[];
  cta: string;
  ctaVariant: 'primary' | 'secondary';
  href: string;
  highlighted: boolean;
  badge?: string;
  perSeat?: boolean;
  contactSales?: boolean;
}

const PLANS: Plan[] = [
  {
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    priceLabel: '$0',
    interval: 'forever',
    description: 'Get started with essential linting',
    features: [
      { text: '38 built-in lint rules', included: true },
      { text: '5 analyses per day', included: true },
      { text: 'VS Code & JetBrains plugins', included: true },
      { text: 'Community support', included: true },
    ],
    cta: 'Get Started',
    ctaVariant: 'secondary',
    href: '/sign-up',
    highlighted: false,
  },
  {
    name: 'Pro',
    monthlyPrice: 19,
    annualPrice: 190,
    priceLabel: '$19',
    interval: '/mo',
    description: 'AI-powered fixes for professionals',
    features: [
      { text: 'Everything in Free, plus:', included: true },
      { text: 'AI-powered fix suggestions', included: true },
      { text: 'Unlimited analyses', included: true },
      { text: 'API & CLI access', included: true },
      { text: 'Email support', included: true },
    ],
    cta: 'Start Pro',
    ctaVariant: 'primary',
    href: '/sign-up?plan=pro',
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    name: 'Team',
    monthlyPrice: 49,
    annualPrice: 490,
    priceLabel: '$49',
    interval: '/mo per seat',
    description: 'Collaboration for growing teams',
    features: [
      { text: 'Everything in Pro, plus:', included: true },
      { text: 'Custom lint rules', included: true },
      { text: 'Team dashboard & analytics', included: true },
      { text: 'Member management', included: true },
      { text: 'Priority support', included: true },
    ],
    cta: 'Start Team',
    ctaVariant: 'primary',
    href: '/sign-up?plan=team',
    highlighted: false,
    perSeat: true,
  },
  {
    name: 'Enterprise',
    monthlyPrice: null,
    annualPrice: null,
    priceLabel: 'Custom',
    interval: '',
    description: 'For organizations with advanced needs',
    features: [
      { text: 'Everything in Team, plus:', included: true },
      { text: 'Audit logging', included: true },
      { text: 'SAML SSO', included: true },
      { text: 'Dedicated support', included: true },
      { text: 'SLA guarantee', included: true },
    ],
    cta: 'Contact Sales',
    ctaVariant: 'secondary',
    href: 'mailto:sales@layerlint.ai',
    highlighted: false,
    contactSales: true,
  },
];

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-ll-success shrink-0 mt-0.5"
    >
      <polyline points="3 8 6.5 11.5 13 4.5" />
    </svg>
  );
}

function formatPrice(plan: Plan, interval: BillingInterval): string {
  if (plan.monthlyPrice === null) return 'Custom';
  if (plan.monthlyPrice === 0) return '$0';
  if (interval === 'annual' && plan.annualPrice !== null) {
    return `$${plan.annualPrice}`;
  }
  return `$${plan.monthlyPrice}`;
}

function formatInterval(plan: Plan, interval: BillingInterval): string {
  if (plan.monthlyPrice === null) return '';
  if (plan.monthlyPrice === 0) return 'forever';
  const suffix = plan.perSeat ? ' per seat' : '';
  return interval === 'annual' ? `/yr${suffix}` : `/mo${suffix}`;
}

function getOriginalMonthlyLabel(plan: Plan): string | null {
  if (plan.monthlyPrice === null || plan.monthlyPrice === 0 || plan.annualPrice === null) {
    return null;
  }
  const suffix = plan.perSeat ? '/mo per seat' : '/mo';
  return `$${plan.monthlyPrice}${suffix}`;
}

export function Pricing() {
  const [searchParams] = useSearchParams();
  const checkoutPlan = searchParams.get('checkout');

  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    if (!checkoutPlan || (checkoutPlan !== 'pro' && checkoutPlan !== 'team')) {
      return;
    }

    let cancelled = false;

    async function triggerCheckout() {
      setCheckoutLoading(true);
      setCheckoutError(null);

      try {
        const { data } = await supabase.auth.getSession();
        const accessToken = data.session?.access_token;

        if (!accessToken) {
          if (!cancelled) {
            setCheckoutError('You must be signed in to checkout. Please sign in first.');
            setCheckoutLoading(false);
          }
          return;
        }

        const apiUrl = import.meta.env.VITE_API_URL as string;
        if (!apiUrl) {
          if (!cancelled) {
            setCheckoutError('API URL is not configured.');
            setCheckoutLoading(false);
          }
          return;
        }

        const response = await fetch(`${apiUrl}/v1/billing/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            plan: checkoutPlan,
            interval: 'monthly',
          }),
        });

        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
          throw new Error(
            (body.message as string | undefined) ?? `Checkout failed (${response.status})`,
          );
        }

        const result = (await response.json()) as { checkoutUrl: string };

        if (!cancelled && result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        }
      } catch (err) {
        if (!cancelled) {
          setCheckoutError(err instanceof Error ? err.message : 'An unexpected error occurred');
          setCheckoutLoading(false);
        }
      }
    }

    void triggerCheckout();

    return () => {
      cancelled = true;
    };
  }, [checkoutPlan]);

  if (checkoutLoading) {
    return (
      <div className="w-full max-w-md mx-auto px-4 py-24">
        <div className="bg-ll-surface border border-ll-border rounded-2xl p-8 shadow-[0_24px_80px_rgba(0,0,0,0.5)] text-center">
          <svg
            className="animate-spin h-8 w-8 text-ll-cyan mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-ll-text font-semibold mb-1">Preparing checkout...</p>
          <p className="text-sm text-ll-muted">
            You&apos;ll be redirected to complete your purchase.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-bold text-ll-text mb-4">
          Simple, transparent pricing
        </h1>
        <p className="text-lg text-ll-muted max-w-xl mx-auto">
          Start free, upgrade when you need more power.
        </p>
      </div>

      {/* Checkout error */}
      {checkoutError && (
        <div className="max-w-md mx-auto mb-8">
          <Alert variant="error" message={checkoutError} onDismiss={() => setCheckoutError(null)} />
        </div>
      )}

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-12">
        <span
          className={`text-sm font-medium transition-colors ${
            interval === 'monthly' ? 'text-ll-text' : 'text-ll-muted'
          }`}
        >
          Monthly
        </span>
        <button
          type="button"
          onClick={() => setInterval((prev) => (prev === 'monthly' ? 'annual' : 'monthly'))}
          className="relative w-12 h-6 rounded-full bg-ll-border transition-colors focus:outline-none focus:ring-2 focus:ring-ll-cyan focus:ring-offset-2 focus:ring-offset-ll-bg"
          role="switch"
          aria-checked={interval === 'annual'}
          aria-label="Toggle annual billing"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-ll-cyan transition-transform duration-200 ${
              interval === 'annual' ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
        <span
          className={`text-sm font-medium transition-colors ${
            interval === 'annual' ? 'text-ll-text' : 'text-ll-muted'
          }`}
        >
          Annual
        </span>
        <span className="text-xs font-semibold text-ll-cyan bg-ll-cyan/10 px-2 py-0.5 rounded-full">
          Save ~17%
        </span>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
        {PLANS.map((plan) => {
          const price = formatPrice(plan, interval);
          const intervalLabel = formatInterval(plan, interval);
          const originalMonthly = interval === 'annual' ? getOriginalMonthlyLabel(plan) : null;

          return (
            <div
              key={plan.name}
              className={`relative bg-ll-surface border rounded-2xl p-6 transition-shadow duration-300 hover:shadow-[0_8px_40px_rgba(0,229,200,0.1)] flex flex-col ${
                plan.highlighted ? 'border-ll-cyan/50' : 'border-ll-border'
              }`}
            >
              {plan.badge && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-ll-cyan text-ll-bg text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              )}

              <div className="mb-6">
                <h2 className="text-lg font-bold text-ll-text mb-1">{plan.name}</h2>
                <p className="text-sm text-ll-muted mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-ll-text">{price}</span>
                  {intervalLabel && <span className="text-sm text-ll-muted">{intervalLabel}</span>}
                </div>
                {originalMonthly && (
                  <p className="text-sm text-ll-muted mt-1">
                    <span className="line-through">{originalMonthly}</span>
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex gap-2">
                    <CheckIcon />
                    <span className="text-sm text-ll-text">{feature.text}</span>
                  </li>
                ))}
              </ul>

              {plan.contactSales ? (
                <a
                  href={plan.href}
                  className={`w-full rounded-lg px-4 py-3 transition-all duration-200 flex items-center justify-center text-center font-medium ${
                    plan.ctaVariant === 'primary'
                      ? 'bg-ll-cyan text-ll-bg font-semibold hover:brightness-110 active:brightness-95'
                      : 'bg-transparent border border-ll-border text-ll-text hover:border-ll-cyan hover:text-ll-cyan'
                  }`}
                >
                  {plan.cta}
                </a>
              ) : (
                <Link
                  to={plan.href}
                  className={`w-full rounded-lg px-4 py-3 transition-all duration-200 flex items-center justify-center text-center font-medium ${
                    plan.ctaVariant === 'primary'
                      ? 'bg-ll-cyan text-ll-bg font-semibold hover:brightness-110 active:brightness-95'
                      : 'bg-transparent border border-ll-border text-ll-text hover:border-ll-cyan hover:text-ll-cyan'
                  }`}
                >
                  {plan.cta}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
